import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type PagedResult,
  type ProjectFolderDetail,
  type StructureListItem,
} from '../lib/api';
import { HoverTooltip } from '../lib/HoverTooltip';
import { structureListDisplayName } from '../lib/structureReport';

/**
 * Mirrors the legacy `project_folder/addtoproject.blade.php` page
 * (route `/project/addstructure/{project_folder}`). Shows the structures
 * already in the project and the remaining available structures, each with a
 * quantity stepper, plus a notes field and a "Save and Continue" action.
 */
export function AddStructuresToProjectPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const projectQuery = useQuery({
    queryKey: ['project-folder', projectId],
    queryFn: () => api.get<ProjectFolderDetail>(`/api/v1/project-folders/${projectId}`),
    enabled: Number.isFinite(projectId),
  });

  const structuresQuery = useQuery({
    queryKey: ['structures', { all: false, picker: true }],
    queryFn: () => api.get<PagedResult<StructureListItem>>('/api/v1/structures?pageSize=200'),
  });

  const project = projectQuery.data;
  const structures = useMemo(() => structuresQuery.data?.items ?? [], [structuresQuery.data]);

  const inProjectQuantities = useMemo(() => {
    const map = new Map<number, number>();
    for (const s of project?.structures ?? []) map.set(s.id, s.quantity);
    return map;
  }, [project]);

  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const [notes, setNotes] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qtyFor = (structureId: number) =>
    quantities[structureId] ?? inProjectQuantities.get(structureId) ?? 0;

  const setQty = (structureId: number, value: number) =>
    setQuantities((current) => ({ ...current, [structureId]: Math.max(0, value) }));

  const currentRows = structures.filter((s) => inProjectQuantities.has(s.id));
  const availableRows = structures.filter((s) => !inProjectQuantities.has(s.id));

  const saveAndContinue = async () => {
    if (!project || saving) return;
    setSaving(true);
    setError(null);
    try {
      for (const s of structures) {
        const desired = qtyFor(s.id);
        const existing = inProjectQuantities.get(s.id) ?? 0;
        if (desired === existing) continue;
        if (desired > 0) {
          await api.post(`/api/v1/project-folders/${projectId}/structures`, {
            structureId: s.id,
            quantity: desired,
          });
        } else if (inProjectQuantities.has(s.id)) {
          await api.del(`/api/v1/project-folders/${projectId}/structures/${s.id}`);
        }
      }
      if (notes !== null && notes !== (project.additionalNotes ?? '')) {
        await api.put(`/api/v1/project-folders/${projectId}`, {
          name: project.name,
          city: project.city,
          state: project.state,
          designEngineer: project.designEngineer,
          siteContractor: project.siteContractor,
          additionalNotes: notes,
        });
      }
      // Invalidate on the bare `project-folder` prefix so both the numeric-keyed
      // entry used here and the string-keyed entry used by ProjectSummaryPage
      // (['project-folder', id] where id is the raw string param) are marked
      // stale. Without this the summary page keeps its 30s-fresh cached copy and
      // only shows the new quantities after a manual refresh.
      await queryClient.invalidateQueries({ queryKey: ['project-folder'] });
      await queryClient.invalidateQueries({ queryKey: ['project-folders'] });
      navigate(`/admin/projects/${projectId}/summary`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save structures.');
      setSaving(false);
    }
  };

  if (projectQuery.isLoading || structuresQuery.isLoading) {
    return <Shell><p className="text-center text-muted">Loading…</p></Shell>;
  }
  if (projectQuery.error || !project) {
    return (
      <Shell>
        <div className="alert alert-danger">Project folder could not be loaded.</div>
        <Link to="/admin/projects" className="btn btn-outline-secondary">← Projects</Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="row mt-lg-3 mt-1 mb-lg-3 mb-1">
        <div className="col col-xl-12 col-lg-12 text-center">
          <h1>Add Structures to: {project.name}</h1>
          <p>Select the structures you would like to add to this project folder.</p>
        </div>
      </div>

      <div className="col-sm-12">
        <h1 className="h3">Structures Currently In Project</h1>
        <StructureTable
          rows={currentRows}
          qtyFor={qtyFor}
          setQty={setQty}
          emptyText="No data available in table"
        />

        <h1 className="h3 mt-4">Available Structures Not Added to Project</h1>
        <StructureTable
          rows={availableRows}
          qtyFor={qtyFor}
          setQty={setQty}
          emptyText="No data available in table"
        />
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-lg-8 pt-2">
          <div className="mb-3 text-center">
            <Link to="/newstructure" className="btn btn-sm btn-primary">+ Create New Structure</Link>
          </div>
          <div className="mb-3">
            <label className="form-label fs-5" htmlFor="notes">
              Additional Requirements or Notes{' '}
              <HoverTooltip
                text="Are there any additional details you think we should know to better match you with a structure?"
                placement="right"
                maxWidth={320}
              >
                <img src="/images/ICON/Icon_Info_Green_Filled_Small.png" alt="Additional requirements information" />
              </HoverTooltip>
            </label>
            <textarea
              id="notes"
              name="notes"
              maxLength={160}
              rows={5}
              className="form-control"
              placeholder="OPTIONAL: Add any additional information we should know (i.e. special requirements, project details, etc). 160 character maximum."
              value={notes ?? project.additionalNotes ?? ''}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <div className="col-12 text-center">
          {error && <div className="alert alert-danger">{error}</div>}
          <button type="button" className="btn btn-primary" disabled={saving} onClick={saveAndContinue}>
            {saving ? 'Saving…' : 'Save and Continue'}
          </button>
        </div>
      </div>
    </Shell>
  );
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function StructureTable({
  rows,
  qtyFor,
  setQty,
  emptyText,
}: {
  rows: StructureListItem[];
  qtyFor: (id: number) => number;
  setQty: (id: number, value: number) => void;
  emptyText: string;
}) {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((s) =>
      [
        structureListDisplayName(s),
        s.structureTypeName ?? '',
        s.styleNumber ?? s.foundryPartNumber ?? '',
      ].some((v) => v.toLowerCase().includes(term)),
    );
  }, [rows, search]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const start = clampedPage * pageSize;
  const visible = filtered.slice(start, start + pageSize);
  const showingFrom = total === 0 ? 0 : start + 1;
  const showingTo = Math.min(start + pageSize, total);

  return (
    <div className="border rounded p-3 mb-2">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
        <label className="d-flex align-items-center gap-2 mb-0 small">
          Show
          <select
            className="form-select form-select-sm"
            style={{ width: 80 }}
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          entries
        </label>
        <label className="d-flex align-items-center gap-2 mb-0 small">
          Search:
          <input
            type="search"
            className="form-control form-control-sm"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
        </label>
      </div>

      <div className="table-responsive">
        <table className="table align-middle w-100 mb-0">
          <thead>
            <tr>
              <th>Structure Name</th>
              <th>Draft</th>
              <th>Type</th>
              <th>Oil Filter</th>
              <th>Style Number</th>
              <th>Last Updated</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-3">{emptyText}</td>
              </tr>
            ) : (
              visible.map((s) => (
                <tr key={s.id}>
                  <td>{structureListDisplayName(s)}</td>
                  <td>{s.isDraft ? 'Yes' : 'No'}</td>
                  <td>{s.permanent ? 'Permanent' : 'Temporary'}</td>
                  <td>{s.oilFiltration ? 'Yes' : 'No'}</td>
                  <td>{s.styleNumber ?? s.foundryPartNumber ?? '—'}</td>
                  <td>{formatDate(s.updatedAt)}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-none d-md-inline me-2"
                        onClick={() => setQty(s.id, qtyFor(s.id) - 1)}
                      >
                        −
                      </button>
                      <div style={{ maxWidth: 100 }}>
                        <input
                          type="number"
                          min={0}
                          className="form-control px-1"
                          aria-label={`Quantity for ${structureListDisplayName(s)}`}
                          value={qtyFor(s.id)}
                          onChange={(e) => setQty(s.id, Math.max(0, Number(e.target.value) || 0))}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary d-none d-md-inline ms-2"
                        onClick={() => setQty(s.id, qtyFor(s.id) + 1)}
                      >
                        +
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
        <span className="text-muted small">
          Showing {showingFrom} to {showingTo} of {total} entries
        </span>
        <div className="btn-group">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={clampedPage <= 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ‹
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={clampedPage >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return <div className="container-fluid">{children}</div>;
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
}
